import { type ComponentType, type ReactElement, createContext, useContext } from "react";

/**
 * Props passed to a {@link FunOverride}.
 *
 * `props` are the props the base component was invoked with.
 * `Original` renders the untouched base component, optionally patching props,
 * so an override can enhance, wrap, or fully replace it.
 */
export interface FunRenderProps<P> {
  readonly props: P;
  readonly Original: (patch?: Partial<P>) => ReactElement | null;
}

/**
 * An override for a single component slot. Returns any element, so returning a
 * dismissable card (or anything else) in place of the original is just a matter
 * of what the override chooses to render.
 */
export type FunOverride<P> = (renderProps: FunRenderProps<P>) => ReactElement | null;

/**
 * An opaque, string-keyed bag of overrides supplied through {@link FunContext}.
 *
 * `ui` does not know which slots exist or their props. The application layer
 * defines its own typed slot registry and provides a value assignable to this.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FunOverrideMap = Readonly<Record<string, FunOverride<any>>>;

/**
 * Client-side-only playful behavior counters (e.g. cookies eaten, clicks).
 * Persisted per-device; never sent to a server and never patient data.
 */
export interface FunMeters {
  get(id: string): number;
  increment(id: string, by?: number): void;
  reset(id: string): void;
  subscribe(id: string, callback: (value: number) => void): () => void;
}

const inertMeters: FunMeters = {
  get: () => 0,
  increment: () => {},
  reset: () => {},
  subscribe: () => () => {},
};

export interface FunContextValue {
  /**
   * The active occasion as an opaque string owned by the application. An empty
   * string means no occasion is active. `ui` does not enumerate occasions; the
   * concrete union lives alongside the overrides in the app layer.
   */
  readonly occasion: string;
  /** Component-slot overrides for the active occasion. Empty when no occasion is active. */
  readonly overrides: FunOverrideMap;
  /** Per-device behavior counters. Inert when no occasion is active. */
  readonly meters: FunMeters;
}

const inertContext: FunContextValue = {
  occasion: "",
  overrides: {},
  meters: inertMeters,
};

export const FunContext = createContext<FunContextValue>(inertContext);

export const useFun = () => useContext(FunContext);

/**
 * Wraps a base component so that, when the active occasion overrides its slot,
 * the override renders instead. When there is no override, the original renders
 * untouched, so the base component body never contains occasion logic.
 *
 * `slot` is a plain string. The props contract is driven entirely by the base
 * component's own props `P`, so `ui` never needs to know which occasions exist.
 *
 * @example
 * export const Spinner = fun("spinner", (props: SpinnerProps) => {
 *   return <CoreSpinner {...toNativeProps(props)} />;
 * });
 */
export function fun<P extends object>(slot: string, Original: ComponentType<P>): FunComponentType<P> {
  const Wrapped = (props: P) => {
    const { overrides } = useContext(FunContext);
    const override = overrides[slot] as FunOverride<P> | undefined;

    if (!override) {
      return <Original {...props} />;
    }

    return override({
      props,
      Original: (patch?: Partial<P>) => <Original {...props} {...patch} />,
    });
  };

  Wrapped.displayName = `fun(${slot})`;
  Wrapped.Original = Original;
  return Wrapped;
}

export type FunComponentType<P> = ComponentType<P> & { Original: ComponentType<P> };
